import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Trash2, Search, Heart, MessageCircle, Tag, Pencil, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { AdminForumTopicsTab } from './AdminForumTopicsTab';

const BACKEND_URL = '';

export function AdminForumTab() {
  const [view, setView] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editPost, setEditPost] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '', topics: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum`, { credentials: 'include' });
      if (res.ok) setPosts(await res.json());
    } catch { toast.error('Failed to load forum posts'); }
  };

  const handleDelete = async (postId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/${postId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(data.message);
      setDeleteConfirm(null);
      loadPosts();
    } catch { toast.error('Delete failed'); }
  };

  const openEdit = (post) => {
    setEditPost(post);
    setEditForm({
      title: post.title || '',
      content: post.content || '',
      topics: (post.topics || []).join(', '),
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) { toast.error('Title is required'); return; }
    if (!editForm.content.trim()) { toast.error('Content is required'); return; }
    setSaving(true);
    try {
      const topics = editForm.topics.split(',').map(t => t.trim()).filter(Boolean);
      const res = await fetch(`${BACKEND_URL}/api/admin/forum/${editPost.post_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editForm.title, content: editForm.content, topics }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Post updated');
      setEditPost(null);
      loadPosts();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const filtered = posts.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.title?.toLowerCase().includes(q) ||
      p.content?.toLowerCase().includes(q) ||
      p.author_name?.toLowerCase().includes(q) ||
      (p.topics || []).some(t => t.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-slate-200" data-testid="forum-subnav">
        <button onClick={() => setView('posts')} data-testid="forum-subnav-posts"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === 'posts'
              ? 'border-brand-navy text-brand-navy'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}>
          <MessageSquare className="h-3.5 w-3.5 inline mr-1.5" /> Posts
        </button>
        <button onClick={() => setView('topics')} data-testid="forum-subnav-topics"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === 'topics'
              ? 'border-brand-navy text-brand-navy'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}>
          <Hash className="h-3.5 w-3.5 inline mr-1.5" /> Topics
        </button>
      </div>

      {view === 'topics' ? <AdminForumTopicsTab /> : (
        <>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">All Forum Posts</h3>
          <p className="text-sm text-slate-500">{posts.length} total posts</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="forum-search" placeholder="Search posts..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-8 w-64 h-9" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No forum posts found</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(post => (
            <Card key={post.post_id} data-testid={`admin-post-${post.post_id}`} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-slate-800">{post.title || 'Untitled Post'}</h4>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{post.content}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                    <span>By {post.author_name}</span>
                    <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{post.like_count || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{post.comment_count || 0}</span>
                    {(post.topics || []).length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />{post.topics.join(', ')}
                      </span>
                    )}
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    {post.edited_by_admin && (
                      <span className="text-amber-500 font-medium">Edited by admin</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button data-testid={`edit-post-${post.post_id}`} onClick={() => openEdit(post)}
                    size="sm" variant="ghost" className="text-slate-500 hover:text-brand-navy hover:bg-brand-navy/10 h-8">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {deleteConfirm === post.post_id ? (
                    <div className="flex gap-1.5 items-center">
                      <span className="text-xs text-red-600 font-medium">Delete?</span>
                      <Button data-testid={`confirm-delete-post-${post.post_id}`} onClick={() => handleDelete(post.post_id)}
                        size="sm" variant="destructive" className="h-7 px-2 text-xs">Yes</Button>
                      <Button data-testid={`cancel-delete-post-${post.post_id}`} onClick={() => setDeleteConfirm(null)}
                        size="sm" variant="outline" className="h-7 px-2 text-xs">No</Button>
                    </div>
                  ) : (
                    <Button data-testid={`delete-post-${post.post_id}`} onClick={() => setDeleteConfirm(post.post_id)}
                      size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Post Dialog */}
      <Dialog open={!!editPost} onOpenChange={(open) => { if (!open) setEditPost(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-post-dialog">
          <DialogHeader>
            <DialogTitle>Edit Forum Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Title</Label>
              <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                data-testid="edit-post-title" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                rows={6} className="min-h-[120px]" data-testid="edit-post-content" />
            </div>
            <div>
              <Label>Topics (comma-separated)</Label>
              <Input value={editForm.topics} onChange={e => setEditForm(f => ({ ...f, topics: e.target.value }))}
                placeholder="e.g. Sustainability, Net Zero, ESG" data-testid="edit-post-topics" />
            </div>
            <Button onClick={handleSaveEdit} disabled={saving}
              className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-post-edit-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
